import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, X, ArrowLeft, ArrowRight, Sparkles, Check, CheckCircle2, Zap, CalendarDays, IndianRupee, Ban, ShieldAlert } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";

const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

/**
 * 4-step Rate Change Wizard: units → date range → rate → restrictions.
 * Pushes to /pms/properties/{pid}/rates/wizard AND (if any restrictions
 * were toggled) /pms/properties/{pid}/restrictions — both fan out to
 * every connected OTA via the sync engine broadcasts.
 */
export default function RateChangeWizard({ pid, propertyName, units, cityDefault, connectedChannels = 0, onClose, onApplied }) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(new Set(units.map((u) => u.id)));
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10);
  });
  const [rate, setRate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Step 4 — Restrictions bundle (Stop Sell / CTA / CTD / MinLOS / MaxStay)
  const [restr, setRestr] = useState({ stop_sell: false, cta: false, cod: false, min_nights: 0, max_nights: 0 });
  const hasRestrictions = restr.stop_sell || restr.cta || restr.cod || restr.min_nights > 0 || restr.max_nights > 0;

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((s) => s.size === units.length ? new Set() : new Set(units.map((u) => u.id)));

  const nights = useMemo(() => {
    if (!start || !end) return 0;
    return Math.max(daysBetween(start, end), 0);
  }, [start, end]);
  const cells = selected.size * nights;
  const rateNum = parseFloat(String(rate).replace(/[^\d.]/g, ""));
  const invalidRange = !start || !end || new Date(end) < new Date(start);

  const next = () => {
    if (step === 1 && selected.size === 0) return toast.error("Pick at least one unit — or tap 'All units' to include the whole property.");
    if (step === 2 && invalidRange) return toast.error("End date must be on or after the start date.");
    if (step === 3 && (!rateNum || rateNum <= 0)) return toast.error("Enter a valid rate greater than zero.");
    setStep((s) => Math.min(s + 1, 4));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const apply = async () => {
    if (!rateNum || rateNum <= 0) return toast.error("Enter a valid rate greater than zero.");
    setBusy(true);
    try {
      const body = {
        unit_ids: selected.size === units.length ? [] : Array.from(selected),
        start, end, rate: rateNum, note: note.trim(),
      };
      const { data } = await api.post(`/pms/properties/${pid}/rates/wizard`, body);
      // If any restriction is set, PUT them in a second call (per-unit, per-date matrix).
      let restrSummary = "";
      // Cert §5/§6/§9: DELTA semantics — only include restrictions the
      // host actually enabled. False/0 means "not touched" and would
      // otherwise make Channex flag the update as snapshot-based.
      if (hasRestrictions) {
        const targetUnits = selected.size === units.length ? units.map((u) => u.id) : Array.from(selected);
        const dates = [];
        const s = new Date(start), e = new Date(end);
        for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) dates.push(d.toISOString().slice(0, 10));
        const updates = [];
        targetUnits.forEach((uid) => dates.forEach((date) => {
          const row = { unit_id: uid, date };
          if (restr.stop_sell) row.stopsell = true;
          if (restr.cta) row.coa = true;
          if (restr.cod) row.cod = true;
          if (restr.min_nights > 0) row.min_nights = restr.min_nights;
          if (restr.max_nights > 0) row.max_nights = restr.max_nights;
          updates.push(row);
        }));
        try {
          const { data: rd } = await api.put(`/pms/properties/${pid}/restrictions`, { updates });
          restrSummary = ` + ${rd.saved} restriction cells`;
        } catch (er) { toast.error(`Rates saved, but restrictions failed: ${errMsg(er)}`); }
      }
      toast.success(`₹${fmt(rateNum)} applied to ${data.unit_count} unit${data.unit_count > 1 ? "s" : ""} across ${data.date_count} night${data.date_count > 1 ? "s" : ""}${restrSummary} · broadcast to ${data.channels_broadcast} channel${data.channels_broadcast === 1 ? "" : "s"}`);
      onApplied?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <Portal>
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div data-testid="rc-wizard" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white border-b border-slate-200 px-7 py-5 flex items-center justify-between z-10">
          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2"><Sparkles size={18} className="brand-pink" /> Rate Change Wizard</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">{propertyName} · pushes to Atiti + {connectedChannels} connected channel{connectedChannels === 1 ? "" : "s"}</p>
          </div>
          <button data-testid="rc-wizard-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </header>

        {/* Steps rail */}
        <div className="flex items-center gap-2 px-7 pt-5">
          {["Select units", "Pick dates", "Set rate", "Restrictions"].map((label, i) => {
            const idx = i + 1;
            const done = step > idx;
            const active = step === idx;
            return (
              <div key={label} className="flex items-center gap-2 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-[#4F46E5] text-white" : "bg-slate-100 text-slate-400"}`}>
                  {done ? <Check size={12} /> : idx}
                </div>
                <span className={`text-[11px] font-semibold ${active ? "text-slate-900" : "text-slate-400"}`}>{label}</span>
                {idx < 4 && <div className={`flex-1 h-[2px] ${done ? "bg-emerald-500" : "bg-slate-100"}`} />}
              </div>
            );
          })}
        </div>

        <div className="px-7 py-6">
          {step === 1 && (
            <section data-testid="rc-wizard-step-1">
              <h3 className="font-display font-bold text-slate-900">Which units are we re-pricing?</h3>
              <p className="text-xs text-slate-500 mt-1">Pick one, a few, or every unit. Untouched units keep their current base rate.</p>
              <button data-testid="rc-wizard-select-all" onClick={toggleAll}
                className="mt-4 text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-400 font-semibold text-slate-700">
                {selected.size === units.length ? "Clear all" : "Select all units"}
              </button>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                {units.map((u) => {
                  const on = selected.has(u.id);
                  return (
                    <button key={u.id} data-testid={`rc-wizard-unit-${u.id}`} onClick={() => toggle(u.id)}
                      className={`px-3 py-2.5 rounded-xl text-xs font-semibold border transition-all text-left ${on ? "bg-[#EEF2FF] border-[#4F46E5] text-[#4F46E5]" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                      <div className="flex items-center justify-between">
                        <span>Unit {u.name}</span>
                        {on && <Check size={12} />}
                      </div>
                      <p className="text-[10px] font-normal text-slate-400 mt-1">Base ₹{fmt(u.base_rate ?? u.suggested_rate ?? cityDefault)}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-4 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                <b>{selected.size}</b> unit{selected.size === 1 ? "" : "s"} selected · {units.length - selected.size} untouched
              </p>
            </section>
          )}

          {step === 2 && (
            <section data-testid="rc-wizard-step-2">
              <h3 className="font-display font-bold text-slate-900">For which nights?</h3>
              <p className="text-xs text-slate-500 mt-1">Rates apply from the check-in date up to (and including) the last night. After this window ends, rates automatically revert to each unit's base rate.</p>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <label className="text-xs">
                  <span className="text-slate-500 font-semibold">From</span>
                  <input data-testid="rc-wizard-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
                    className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
                </label>
                <label className="text-xs">
                  <span className="text-slate-500 font-semibold">To (last night)</span>
                  <input data-testid="rc-wizard-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} min={start}
                    className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
                </label>
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {[
                  ["This weekend", 0, 2, (d) => { const s = new Date(); const day = s.getDay(); const to = (5 - day + 7) % 7; s.setDate(s.getDate() + to); return [s.toISOString().slice(0, 10), new Date(s.getTime() + 86400000).toISOString().slice(0, 10)]; }],
                  ["Next 7 nights", 0, 6],
                  ["Next 14 nights", 0, 13],
                  ["This month", "month"],
                ].map(([label, a, b, fn]) => (
                  <button key={label} data-testid={`rc-wizard-quick-${label.replace(/\s/g, "-")}`}
                    onClick={() => {
                      if (fn) { const [s, e] = fn(); setStart(s); setEnd(e); return; }
                      if (a === "month") {
                        const now = new Date();
                        const s = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                        const e = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
                        setStart(s); setEnd(e); return;
                      }
                      const s = new Date(); const e = new Date(); e.setDate(e.getDate() + b);
                      setStart(s.toISOString().slice(0, 10)); setEnd(e.toISOString().slice(0, 10));
                    }}
                    className="text-[11px] px-3 py-1 rounded-full border border-slate-200 hover:border-slate-400 text-slate-600">
                    {label}
                  </button>
                ))}
              </div>
              <p className={`text-[11px] mt-4 rounded-lg px-3 py-2 ${invalidRange ? "bg-red-50 border border-red-200 text-red-700" : "bg-slate-50 border border-slate-100 text-slate-600"}`}>
                {invalidRange ? "End date must be on or after start date." : <><CalendarDays size={11} className="inline mr-1" /> <b>{nights}</b> night{nights === 1 ? "" : "s"} · {selected.size} unit{selected.size === 1 ? "" : "s"} = <b>{fmt(cells)}</b> rate cells</>}
              </p>
            </section>
          )}

          {step === 3 && (
            <section data-testid="rc-wizard-step-3">
              <h3 className="font-display font-bold text-slate-900">What's the new nightly rate?</h3>
              <p className="text-xs text-slate-500 mt-1">This exact amount replaces every existing rate for the selected units and nights, and pushes to every connected channel immediately.</p>
              <label className="block mt-4">
                <span className="text-[11px] text-slate-500 font-semibold">Rate per night (₹)</span>
                <div className="relative mt-1">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="rc-wizard-rate" type="text" inputMode="decimal" autoFocus placeholder="e.g. 4500"
                    value={rate} onChange={(e) => setRate(e.target.value.replace(/[^\d.]/g, ""))}
                    className="w-full pl-9 pr-3 py-3 text-2xl font-display font-black text-slate-900 border-2 border-slate-200 rounded-xl focus:border-[#4F46E5] focus:outline-none" />
                </div>
              </label>
              <label className="block mt-4">
                <span className="text-[11px] text-slate-500 font-semibold">Note (optional, saved on the audit log)</span>
                <input data-testid="rc-wizard-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Diwali long-weekend push"
                  className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
              </label>
              <div className="mt-5 bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider brand-pink">Preview</p>
                <p className="text-sm text-slate-800 mt-2 leading-relaxed">
                  ₹<b>{fmt(rateNum || 0)}</b>/night · <b>{selected.size}</b> unit{selected.size === 1 ? "" : "s"} · <b>{start}</b> → <b>{end}</b> · <b>{nights}</b> night{nights === 1 ? "" : "s"} = <b>{fmt(cells)}</b> rate cell{cells === 1 ? "" : "s"}
                </p>
                <p className="text-[11px] text-slate-600 mt-2 flex items-center gap-1.5"><Zap size={11} className="brand-pink" /> Pushes to Atiti + <b>{connectedChannels}</b> connected channel{connectedChannels === 1 ? "" : "s"} in one call.</p>
              </div>
            </section>
          )}

          {step === 4 && (
            <section data-testid="rc-wizard-step-4">
              <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><ShieldAlert size={16} className="text-red-500" /> Any restrictions for these nights?</h3>
              <p className="text-xs text-slate-500 mt-1">Optional — leave everything off to only push the new rate. Anything you enable here applies to the same {selected.size} unit{selected.size === 1 ? "" : "s"} × {nights} night{nights === 1 ? "" : "s"} and broadcasts to every connected OTA.</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                {[
                  { key: "stop_sell", label: "Stop Sell", desc: "Freeze sales on every channel", color: "red" },
                  { key: "cta", label: "Closed to Arrival", desc: "No new stays can start", color: "orange" },
                  { key: "cod", label: "Closed to Departure", desc: "No stays may end", color: "amber" },
                ].map((r) => {
                  const on = !!restr[r.key];
                  return (
                    <button key={r.key} data-testid={`rc-wizard-restr-${r.key}`}
                      onClick={() => setRestr((x) => ({ ...x, [r.key]: !on }))}
                      className={`p-4 rounded-xl border text-left transition-all ${on ? `bg-${r.color}-50 border-${r.color}-400` : "bg-white border-slate-200 hover:border-slate-400"}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-display font-bold ${on ? `text-${r.color}-700` : "text-slate-800"}`}>{r.label}</span>
                        {on ? <Ban size={16} className={`text-${r.color}-500`} /> : <span className="text-[10px] font-semibold text-slate-300">OFF</span>}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">{r.desc}</p>
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <label className="text-xs">
                  <span className="text-slate-500 font-semibold">Minimum nights (MinLOS)</span>
                  <input data-testid="rc-wizard-min-nights" type="number" min="0" max="60"
                    value={restr.min_nights || ""}
                    onChange={(e) => setRestr((x) => ({ ...x, min_nights: parseInt(e.target.value || "0", 10) }))}
                    placeholder="Leave blank for none"
                    className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
                </label>
                <label className="text-xs">
                  <span className="text-slate-500 font-semibold">Maximum nights</span>
                  <input data-testid="rc-wizard-max-nights" type="number" min="0" max="60"
                    value={restr.max_nights || ""}
                    onChange={(e) => setRestr((x) => ({ ...x, max_nights: parseInt(e.target.value || "0", 10) }))}
                    placeholder="Leave blank for none"
                    className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
                </label>
              </div>

              <div className="mt-5 bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider brand-pink">Final preview</p>
                <p className="text-sm text-slate-800 mt-2 leading-relaxed">
                  ₹<b>{fmt(rateNum || 0)}</b>/night · <b>{selected.size}</b> unit{selected.size === 1 ? "" : "s"} · <b>{nights}</b> night{nights === 1 ? "" : "s"}
                  {hasRestrictions && <> · <b className="text-red-600">Restrictions</b>: {[restr.stop_sell && "StopSell", restr.cta && "CTA", restr.cod && "CTD", restr.min_nights > 0 && `MinLOS ${restr.min_nights}`, restr.max_nights > 0 && `MaxStay ${restr.max_nights}`].filter(Boolean).join(" · ")}</>}
                </p>
                <p className="text-[11px] text-slate-600 mt-2 flex items-center gap-1.5"><Zap size={11} className="brand-pink" /> Pushes rates{hasRestrictions && " + restrictions"} to Atiti + <b>{connectedChannels}</b> connected channel{connectedChannels === 1 ? "" : "s"}.</p>
              </div>
            </section>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-7 py-4 flex items-center justify-between">
          <button data-testid="rc-wizard-back" onClick={back} disabled={step === 1 || busy}
            className="text-xs px-4 py-2 rounded-full border border-slate-200 hover:border-slate-400 disabled:opacity-40 flex items-center gap-1.5">
            <ArrowLeft size={12} /> Back
          </button>
          <p className="text-[11px] text-slate-400">Step {step} of 4</p>
          {step < 4 ? (
            <button data-testid="rc-wizard-next" onClick={next} disabled={busy}
              className="btn-brand rounded-full px-5 py-2 text-xs font-semibold flex items-center gap-1.5">
              Next <ArrowRight size={12} />
            </button>
          ) : (
            <button data-testid="rc-wizard-apply" onClick={apply} disabled={busy || !rateNum || rateNum <= 0}
              className="btn-brand rounded-full px-5 py-2 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle2 size={12} />} Apply {hasRestrictions ? "+ restrictions " : ""}& push to OTAs
            </button>
          )}
        </footer>
      </div>
    </div>
    </Portal>
  );
}
